import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Modal } from 'react-style-guide';
import PropTypes from 'prop-types';
import {
  DefaultThumbnailSize,
  Thumbnail2d,
  ThumbnailTypes,
  ThumbnailFormat
} from 'roblox-thumbnails';
import GameName from '../components/GameName';
import CardInfo from '../components/CardInfo';
import NativeAd from '../components/NativeAd';
import gameTileUtils from '../utils/gameTileUtils';
import Players from '../../gamePlayers/components/Players';
import PlayerInteractionModal from '../../gamePlayers/containers/PlayerInteractionModal';
import friendsDataUtilities from '../../userData/utils/friendsDataUtilities';
import browserUtils from '../../../../ts/react/common/utils/browserUtils';

const GameTile = React.forwardRef((props, ref) => {
  const {
    game,
    pageType,
    index,
    sortName,
    pageContext,
    sortPosition,
    dateTime,
    friendsData,
    gameDetailPlaceId,
    isHidden,
    buildEventProperties
  } = props;
  const { universeId } = game;
  const gameReferralUrl = gameTileUtils.getGameReferralUrl(
    game,
    pageType,
    index,
    sortName,
    pageContext,
    sortPosition,
    dateTime,
    gameDetailPlaceId
  );
  game.referralUrl = gameReferralUrl;

  const [userFriendsData, setUserFriendsData] = useState(friendsData);
  const [tempFriendsData, setTempFriendsData] = useState([]);
  const [friendsInGame, setFriendsInGame] = useState(
    friendsDataUtilities.getFriendsInGame(friendsData, universeId)
  );
  const [isModalShown, setModalShown] = useState(false);
  const previousModalStatus = useRef();
  const handlePlayerInteraction = () => {
    setModalShown(true);
    previousModalStatus.current = true;
  };

  const clientReferralUrl = buildEventProperties
    ? browserUtils.buildGameDetailUrl(game.placeId, game.name, buildEventProperties(game, index))
    : gameReferralUrl;

  const dismissModal = () => {
    if (tempFriendsData?.length) {
      setFriendsInGame(friendsDataUtilities.getFriendsInGame(tempFriendsData, universeId));
      setUserFriendsData(tempFriendsData);
      setTempFriendsData([]);
    }
    setModalShown(false);
    previousModalStatus.current = false;
  };
  const handlePresenceUpdate = useCallback(
    event => {
      if (event?.detail) {
        const updatedFriendsData = friendsDataUtilities.getPresencesUpdateData(
          event.detail,
          friendsData,
          friendsInGame,
          universeId
        );
        if (updatedFriendsData?.length) {
          if (previousModalStatus.current) {
            setTempFriendsData(updatedFriendsData);
          } else {
            setFriendsInGame(friendsDataUtilities.getFriendsInGame(updatedFriendsData, universeId));
            setUserFriendsData(updatedFriendsData);
          }
        }
      }
    },
    [friendsData, friendsInGame, universeId]
  );

  const gameIcon = (
    <div className='game-card-thumb-container'>
      <Thumbnail2d
        type={ThumbnailTypes.gameIcon}
        size={DefaultThumbnailSize}
        targetId={game.universeId}
        containerClass='game-card-thumb'
        format={ThumbnailFormat.jpeg}
        altName={game.name}
      />
    </div>
  );

  useEffect(() => {
    if (friendsData?.length) {
      document.addEventListener('Roblox.Presence.Update', handlePresenceUpdate);
    }

    return () => {
      if (friendsData?.length) {
        document.removeEventListener('Roblox.Presence.Update', handlePresenceUpdate);
      }
    };
  }, [friendsData, friendsInGame, handlePresenceUpdate, universeId]);
  return (
    <div className='game-card-container' ref={ref}>
      {friendsInGame.length > 0 && (
        <React.Fragment>
          <Link
            tabIndex={isHidden ? -1 : 0}
            aria-hidden={isHidden}
            url={clientReferralUrl}
            className='game-card-link'
            id={game.universeId}>
            {gameIcon}
          </Link>
          <GameName game={game} isOneLine handlePlayerInteraction={handlePlayerInteraction} />
          <Players
            handlePlayerInteraction={handlePlayerInteraction}
            friendsData={userFriendsData}
            friendsInGame={friendsInGame}
          />
          <Modal show={isModalShown} onHide={dismissModal} size='lg'>
            <PlayerInteractionModal
              friendsData={userFriendsData}
              friendsInGame={friendsInGame}
              game={game}
              dismissModal={dismissModal}
              {...props}
            />
          </Modal>
        </React.Fragment>
      )}
      {friendsInGame.length === 0 && (
        <Link
          tabIndex={isHidden ? -1 : 0}
          aria-hidden={isHidden}
          url={clientReferralUrl}
          className='game-card-link'
          id={game.universeId}>
          {gameIcon}
          <GameName game={game} />
          {game.isShowSponsoredLabel && <NativeAd {...props} />}
          {!game.isShowSponsoredLabel && <CardInfo game={game} />}
        </Link>
      )}
    </div>
  );
});

GameTile.defaultProps = {
  sortName: null,
  sortPosition: -1,
  pageContext: { pageId: null },
  dateTime: null,
  friendsData: [],
  buildEventProperties: undefined,
  gameDetailPlaceId: null,
  isHidden: false
};
GameTile.propTypes = {
  game: PropTypes.shape({
    universeId: PropTypes.number,
    placeId: PropTypes.number,
    name: PropTypes.string,
    playerCount: PropTypes.number,
    isShowSponsoredLabel: PropTypes.bool,
    nativeAdData: PropTypes.string,
    referralUrl: PropTypes.string
  }).isRequired,
  buildEventProperties: PropTypes.func,
  pageType: PropTypes.string.isRequired,
  isHidden: PropTypes.bool,
  index: PropTypes.number.isRequired,
  sortName: PropTypes.string,
  pageContext: PropTypes.shape({ pageId: PropTypes.string }),
  sortPosition: PropTypes.number,
  dateTime: PropTypes.string,
  friendsData: PropTypes.arrayOf(
    PropTypes.shape({
      presense: PropTypes.shape({ userPresenceType: PropTypes.number }),
      id: PropTypes.number,
      nameForDisplay: PropTypes.string
    })
  ),
  gameDetailPlaceId: PropTypes.number
};
GameTile.displayName = 'GameTile';
export default GameTile;
